import { styled, SxProps, Theme } from '@mui/material';
import { FormEventHandler, PropsWithChildren, type ReactElement } from 'react';

import { Label } from './components/Label';
import { LabelWithTooltip } from './components/LabelWithTooltip';
import { HeadlessLabelWithTooltip } from './components/HeadlessLabelWithTooltip';
import { FieldContainer } from './components/FieldContainer';

const FormColumn = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  flexWrap: 'nowrap',
}));

const ButtonsContainer = styled(FormColumn)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  width: '100%',
}));

const SecondaryText = styled('span')(() => ({
  marginTop: 20,
  fontWeight: 'var(--font-weight-regular)',
  fontSize: 'var(--font-size-large)',
  lineHeight: 'var(--line-height-large)',
}));

const FormElement = styled('form')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'nowrap',
  width: '100%',
  gap: theme.constants.formGap,
  flex: 1,
  margin: 0,
}));

const SideLabel = styled('div')(() => ({}));

const LabelContainer = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
}));

export type FormProps = {
  onSubmit: FormEventHandler<HTMLFormElement>;
  'data-testid'?: string;
  sx?: SxProps<Theme>;
};

export type FormElement = ((
  props: PropsWithChildren<FormProps>
) => ReactElement) & {
  Column: typeof FormColumn;
  ButtonsContainer: typeof ButtonsContainer;
  SecondaryText: typeof SecondaryText;
  Label: typeof Label;
  LabelWithTooltip: typeof LabelWithTooltip;
  HeadlessLabelWithTooltip: typeof HeadlessLabelWithTooltip;
  SideLabel: typeof SideLabel;
  LabelContainer: typeof LabelContainer;
  FieldContainer: typeof FieldContainer;
};

export const Form: FormElement = ({ children, onSubmit, sx, ...rest }) => {
  return (
    <FormElement
      as="form"
      onSubmit={onSubmit}
      data-testid={rest['data-testid']}
      sx={sx}
    >
      {children}
    </FormElement>
  );
};

Form.Column = FormColumn;
Form.ButtonsContainer = ButtonsContainer;
Form.SecondaryText = SecondaryText;
Form.Label = Label;
Form.LabelWithTooltip = LabelWithTooltip;
Form.HeadlessLabelWithTooltip = HeadlessLabelWithTooltip;
Form.SideLabel = SideLabel;
Form.LabelContainer = LabelContainer;
Form.FieldContainer = FieldContainer;
